// Weather-driven dish ranking for the display carousel.
//
// The Last Tribe's menu is entirely meat, so there is no "have a salad" answer
// when it's 40°C — the useful distinction is *relative*: how heavy, oily,
// spicy and piping-hot a dish eats. Dry-grilled and steamed-rice plates sit
// easy in heat; slow-cooked broths, karahis and heavy masala come into their
// own in cold and rain. Every dish is scored against the current reading and
// the best few are returned, so a hot day surfaces the leanest end of the menu
// and a cold night the richest.
//
// Replaces the previous hardcoded if/else buckets, which had been left with
// mismatched copy from an earlier non-meat menu — "Chicken Mandi: extremely
// hydrating in intense heat", "Lamb Qeema: traditional cooling drink",
// "Simple Rosh: protects skin from sun damage" — and could recommend the
// single richest dishes on the menu at peak heat.

import chickenMandi from '../../../assets/food/chicken-mandi.JPG';
import muttonMandi from '../../../assets/food/mutton-mandi.JPG';
import beefAfghaniTikkah from '../../../assets/food/beef-afghani-tikkah.JPG';
import lambNamkeenTikkah from '../../../assets/food/lamb-namkeen-tikkah.JPG';
import seekhKabab from '../../../assets/food/seekh-kabab.JPG';
import beefChapalKabab from '../../../assets/food/beef-chapal-kabab.JPG';
import raanWithKabuliPulao from '../../../assets/food/raan-w-kabuli-pulao.JPG';
import lambQeema from '../../../assets/food/lamb-qeema.JPG';
import chickenKarahi from '../../../assets/food/chicken-karahi.JPG';
import sulemaniKarahi from '../../../assets/food/sulemani-karahi.JPG';
import simpleRosh from '../../../assets/food/simple-rosh.JPG';
import masalaRosh from '../../../assets/food/masala-rosh.JPG';
import mutton from '../../../assets/food/mutton.JPG';

import { feelsLikeTemp, isWet } from '../../weather/utils/feelsLike';

// How many dishes the carousel cycles through.
const MAX_RECOMMENDATIONS = 6;

// Each dish rated 1-5 on the four things that decide whether it suits the
// weather. `style` selects the copy; `appeal` is a house-favourite weight that
// only breaks ties on the many days that sit in the middle of the scale.
const MENU = [
  {
    name: 'Lamb Namkeen Tikkah',
    image: lambNamkeenTikkah,
    heaviness: 2, oiliness: 2, spice: 1, warmth: 3,
    style: 'grill-lean', appeal: 0.8
  },
  {
    name: 'Beef Afghani Tikkah',
    image: beefAfghaniTikkah,
    heaviness: 3, oiliness: 2, spice: 2, warmth: 3,
    style: 'grill-lean', appeal: 0.85
  },
  {
    name: 'Seekh Kabab',
    image: seekhKabab,
    heaviness: 3, oiliness: 3, spice: 3, warmth: 3,
    style: 'grill-lean', appeal: 0.8
  },
  {
    name: 'Chicken Mandi',
    image: chickenMandi,
    heaviness: 3, oiliness: 2, spice: 2, warmth: 3,
    style: 'rice-mild', appeal: 0.9
  },
  {
    name: 'Mutton Mandi',
    image: muttonMandi,
    heaviness: 4, oiliness: 3, spice: 2, warmth: 3,
    style: 'rice-mild', appeal: 0.85
  },
  {
    name: 'Raan with Kabuli Pulao',
    image: raanWithKabuliPulao,
    heaviness: 5, oiliness: 3, spice: 2, warmth: 3,
    style: 'rice-rich', appeal: 0.95
  },
  {
    name: 'Beef Chapal Kabab',
    image: beefChapalKabab,
    heaviness: 4, oiliness: 4, spice: 4, warmth: 3,
    style: 'grill-spiced', appeal: 0.75
  },
  {
    name: 'Chicken Karahi',
    image: chickenKarahi,
    heaviness: 4, oiliness: 4, spice: 4, warmth: 4,
    style: 'curry-rich', appeal: 0.85
  },
  {
    name: 'Sulemani Karahi',
    image: sulemaniKarahi,
    heaviness: 4, oiliness: 5, spice: 4, warmth: 4,
    style: 'curry-rich', appeal: 0.8
  },
  {
    name: 'Lamb Qeema',
    image: lambQeema,
    heaviness: 4, oiliness: 4, spice: 4, warmth: 4,
    style: 'curry-rich', appeal: 0.7
  },
  {
    name: 'Mutton',
    image: mutton,
    heaviness: 4, oiliness: 4, spice: 3, warmth: 4,
    style: 'broth-rich', appeal: 0.7
  },
  {
    name: 'Simple Rosh',
    image: simpleRosh,
    heaviness: 4, oiliness: 5, spice: 1, warmth: 5,
    style: 'broth-rich', appeal: 0.75
  },
  {
    name: 'Masala Rosh',
    image: masalaRosh,
    heaviness: 5, oiliness: 5, spice: 5, warmth: 5,
    style: 'broth-rich', appeal: 0.8
  }
];

const STYLE_COLORS = {
  'grill-lean': '#95E1D3',
  'rice-mild': '#A8E6CF',
  'rice-rich': '#F4A261',
  'grill-spiced': '#FFD93D',
  'curry-rich': '#FF9F1C',
  'broth-rich': '#E76F51'
};

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
// 1-5 rating onto 0-1.
const norm = (rating) => (rating - 1) / 4;

// One number for "how rich does this eat", weighted toward bulk and fat since
// those are what heat actually suppresses the appetite for.
function richnessOf(dish) {
  return dish.heaviness * 0.32
    + dish.oiliness * 0.28
    + dish.spice * 0.20
    + dish.warmth * 0.20;
}

// Apparent temperature onto -1 (cold enough for broth) .. +1 (too hot for
// anything rich). Centred on 26.5°C and saturating at 15°C / 38°C, which is
// the range these Punjab/Islamabad locations actually live in.
function heatFactorOf(feelsLike) {
  if (feelsLike === null) return 0;
  return clamp((feelsLike - 26.5) / 11.5, -1, 1);
}

// Copy bands deliberately line up with weatherParser's own temperature
// buckets, so the left panel can't read "Pleasant" while the carousel says
// it's scorching.
function bandOf(feelsLike) {
  if (feelsLike === null) return 'warm';
  if (feelsLike >= 36) return 'scorching';
  if (feelsLike >= 30) return 'hot';
  if (feelsLike >= 24) return 'warm';
  if (feelsLike >= 18) return 'mild';
  return 'cold';
}

const REASONS = {
  scorching: {
    'grill-lean': 'Dry-grilled with barely any masala — the easiest thing to eat in this heat',
    'rice-mild': 'Steamed and mildly spiced — filling without sitting heavy',
    'rice-rich': 'Fragrant rice makes a big plate manageable even now',
    'grill-spiced': 'Off the grill, with the spice there if you want it',
    'curry-rich': 'Rich and fiery — worth saving for a cooler evening',
    'broth-rich': 'Slow-cooked and heavy — better once the heat drops'
  },
  hot: {
    'grill-lean': 'Lean, grilled and light on oil — right for a hot afternoon',
    'rice-mild': 'Mild spice over rice — satisfying without the weight',
    'rice-rich': 'A proper plate that still works on a warm day',
    'grill-spiced': 'Grilled and well spiced, without the heavy gravy',
    'curry-rich': 'Deep, oily and spicy — heavy going in this heat',
    'broth-rich': 'Built for cold nights, not an afternoon like this'
  },
  warm: {
    'grill-lean': 'Charcoal-grilled — perfect weather to eat it outdoors',
    'rice-mild': 'Comfortable weather for a full mandi platter',
    'rice-rich': 'The centrepiece plate, and the weather to enjoy it',
    'grill-spiced': 'Spiced, fried and best eaten hot off the pan',
    'curry-rich': 'Rich karahi weather — good spice, good evening',
    'broth-rich': 'Slow-cooked and hearty, if you want something substantial'
  },
  mild: {
    'grill-lean': 'Grilled and hot off the coals on a cool evening',
    'rice-mild': 'Warm, generous plate for a mild night',
    'rice-rich': 'A feast plate — ideal for a cool evening out',
    'grill-spiced': 'Fried, spiced and warming as it cools down',
    'curry-rich': 'Spicy, oily karahi — exactly what this weather asks for',
    'broth-rich': 'Slow-cooked in its own fat and served piping hot'
  },
  cold: {
    'grill-lean': 'Straight off the grill while it is cold out',
    'rice-mild': 'A hot, filling plate to take the chill off',
    'rice-rich': 'Roast lamb and hot pulao against the cold',
    'grill-spiced': 'Fried and heavily spiced — warms you from the first bite',
    'curry-rich': 'Deep, spicy and hot — cold-night karahi',
    'broth-rich': 'Slow-cooked lamb in its own broth — the reason for cold nights'
  }
};

const RAIN_REASONS = {
  'grill-lean': 'Hot off the grill while the rain comes down',
  'rice-mild': 'A hot plate of rice and meat for wet weather',
  'rice-rich': 'Roast lamb and hot pulao — proper monsoon eating',
  'grill-spiced': 'Fried, spiced and hot — made for rainy evenings',
  'curry-rich': 'Spicy karahi and rain go together',
  'broth-rich': 'Hot lamb broth while it pours outside'
};

// Several phrasings per band: the overlay rotates the top few dishes every
// 12 s, and repeating one prefix verbatim reads like a stuck screen.
const HEADLINES = {
  scorching: [
    (temp, name) => `${temp}° out there — go light with ${name}`,
    (temp, name) => `Too hot for heavy food — ${name} instead`,
    (temp, name) => `Beat the ${temp}° heat with ${name}`
  ],
  hot: [
    (temp, name) => `Hot one today — ${name} keeps it light`,
    (temp, name) => `${temp}° outside — try ${name}`,
    (temp, name) => `Warm afternoon, easy choice: ${name}`
  ],
  warm: [
    (temp, name) => `Perfect weather for ${name}`,
    (temp, name) => `${temp}° and pleasant — ${name} it is`,
    (temp, name) => `Ideal evening for ${name}`
  ],
  mild: [
    (temp, name) => `Cool evening — good time for ${name}`,
    (temp, name) => `${temp}° out — ${name} hits the spot`,
    (temp, name) => `Crisp night, hot plate of ${name}`
  ],
  cold: [
    (temp, name) => `${temp}° outside — warm up with ${name}`,
    (temp, name) => `Cold night — ${name} is the answer`,
    (temp, name) => `Beat the ${temp}° chill with ${name}`
  ]
};

const RAIN_HEADLINES = [
  (temp, name) => `Rain is in — nothing better than ${name}`,
  (temp, name) => `Wet evening, hot ${name}`,
  (temp, name) => `Monsoon weather calls for ${name}`
];

function scoreDish(dish, context) {
  const { heatFactor, humidity, wet } = context;

  // The core trade-off: heat penalises richness, cold rewards it.
  let score = -heatFactor * (norm(richnessOf(dish)) - 0.5) * 2;

  // Humid heat is what really kills the appetite for oil — 35°C at 30%
  // humidity eats very differently from 35°C at 80%.
  if (heatFactor > 0) {
    score -= 0.35 * norm(dish.oiliness) * clamp((humidity - 55) / 35, 0, 1) * heatFactor;
  }

  // Rain pulls toward hot, spicy comfort food at any temperature.
  if (wet) {
    score += 0.45 * (norm(dish.warmth) * 0.6 + norm(dish.spice) * 0.4);
  }

  // House favourites decide the many days that sit mid-scale, where the
  // weather barely discriminates between dishes.
  score += 0.18 * dish.appeal * (1 - Math.abs(heatFactor));

  return score;
}

export const getFoodRecommendations = (weatherData) => {
  if (!weatherData) return [];

  const feelsLike = feelsLikeTemp(weatherData);
  const heatFactor = heatFactorOf(feelsLike);
  const band = bandOf(feelsLike);
  const wet = isWet(weatherData);
  const context = {
    heatFactor,
    humidity: weatherData.humidity ?? 50,
    wet
  };

  const displayTemp = Math.round(feelsLike ?? weatherData.temperature ?? 0);

  return MENU
    .map((dish) => ({ dish, score: scoreDish(dish, context) }))
    .sort((a, b) => b.score - a.score)
    .slice(0, MAX_RECOMMENDATIONS)
    .map(({ dish }, index) => {
      const headlines = wet ? RAIN_HEADLINES : HEADLINES[band];
      return {
        name: dish.name,
        image: dish.image,
        color: STYLE_COLORS[dish.style],
        reason: (wet ? RAIN_REASONS[dish.style] : REASONS[band][dish.style]),
        // Single-line version for the top-right overlay, so the carousel and
        // the overlay always name the same dish.
        headline: headlines[index % headlines.length](displayTemp, dish.name)
      };
    });
};
